import { BillingPublisherPaymentMethod } from 'respona_api/generated/common_pb';

import { BillingPublisherPayoutSettingsType } from '@ts/billing-publisher.types';

export const formatAmountInCurrency = (amount: number, currency: string): string => {
  if (!currency) return amount.toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

export const formatUsd = (amount: number | string | null | undefined): string => {
  const n = Number(amount ?? 0);
  if (Number.isNaN(n)) return '$0.00';
  return formatAmountInCurrency(n, 'USD').replace(/^US\$/, '$');
};

export const formatEstimatedDelivery = (isoTimestamp: string): string => {
  if (!isoTimestamp) return '';
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

export const formatPayoutTypeLabel = (payoutType: string): string => {
  switch (payoutType) {
    case 'BANK_TRANSFER':
      return 'Local bank transfer';
    case 'SWIFT':
      return 'SWIFT';
    default:
      return payoutType
        .split('_')
        .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
        .join(' ');
  }
};

export const getPayoutMethodLabel = (
  settings: BillingPublisherPayoutSettingsType | undefined
): string => {
  if (!settings) return '—';
  switch (settings.paymentMethod) {
    case BillingPublisherPaymentMethod.PAYPAL:
      return 'PayPal';
    case BillingPublisherPaymentMethod.BANK_ACCOUNT:
      return 'Bank transfer';
    case BillingPublisherPaymentMethod.WISE_ACCOUNT:
      return 'Wise';
    case BillingPublisherPaymentMethod.MANUAL:
      return 'Manual';
    default:
      return '—';
  }
};

export const getWithdrawButtonLabel = (
  settings: BillingPublisherPayoutSettingsType | undefined
): string => {
  if (!settings) return '—';
  switch (settings.paymentMethod) {
    case BillingPublisherPaymentMethod.PAYPAL:
      return 'PayPal';
    case BillingPublisherPaymentMethod.BANK_ACCOUNT:
      return 'Bank';
    case BillingPublisherPaymentMethod.WISE_ACCOUNT:
      return 'Wise';
    case BillingPublisherPaymentMethod.MANUAL:
      return 'Manual';
    default:
      return '—';
  }
};

export const isPayoutMethodConfirmed = (pm: BillingPublisherPayoutSettingsType): boolean => {
  switch (pm.paymentMethod) {
    case BillingPublisherPaymentMethod.PAYPAL:
      return !!pm.payPal?.confirmed;
    case BillingPublisherPaymentMethod.BANK_ACCOUNT:
      return !!pm.bankAccount?.confirmed;
    case BillingPublisherPaymentMethod.WISE_ACCOUNT:
      return !!pm.wiseAccount?.confirmed;
    case BillingPublisherPaymentMethod.MANUAL:
      return true;
    default:
      return false;
  }
};

// Withdrawals routed through Wise (bank account and Wise account) trigger a
// fee preview so the publisher sees the Wise fee, target amount, and ETA
// before confirming the transfer.
export const isWiseFundedPayoutMethod = (
  pm: BillingPublisherPayoutSettingsType | undefined
): boolean =>
  pm?.paymentMethod === BillingPublisherPaymentMethod.BANK_ACCOUNT ||
  pm?.paymentMethod === BillingPublisherPaymentMethod.WISE_ACCOUNT;

export const getWisePayoutRecipient = (
  pm: BillingPublisherPayoutSettingsType | undefined
): { currency: string; recipientId: string } => {
  if (pm?.paymentMethod === BillingPublisherPaymentMethod.BANK_ACCOUNT) {
    return {
      currency: pm.bankAccount?.currency ?? '',
      recipientId: pm.bankAccount?.recipientId ?? '',
    };
  }
  if (pm?.paymentMethod === BillingPublisherPaymentMethod.WISE_ACCOUNT) {
    return { currency: 'USD', recipientId: pm.wiseAccount?.recipientId ?? '' };
  }
  return { currency: '', recipientId: '' };
};

export const mapPaymentMethod = (pm: BillingPublisherPayoutSettingsType | undefined): string => {
  switch (pm?.paymentMethod) {
    case BillingPublisherPaymentMethod.PAYPAL:
      return `PayPal: ${pm?.payPal?.email ?? ''}`;
    case BillingPublisherPaymentMethod.BANK_ACCOUNT: {
      const bank = pm?.bankAccount;
      const summary = bank?.accountSummary || bank?.currency || '';
      return summary ? `Bank: ${summary}` : 'Bank';
    }
    case BillingPublisherPaymentMethod.WISE_ACCOUNT: {
      const summary = pm?.wiseAccount?.accountSummary ?? '';
      return summary ? `Wise: ${summary}` : 'Wise';
    }
    case BillingPublisherPaymentMethod.MANUAL:
      return `Manual: ${pm?.manual?.description ?? ''}`;
    default:
      return '—';
  }
};

export type StatusLabelColor = 'green' | 'orange' | 'red' | 'gray';

export const statusLabelColor = (status: string): StatusLabelColor => {
  if (['Approved', 'Paid'].includes(status)) return 'green';
  if (['Pending approval', 'Refunded', 'Processing'].includes(status)) return 'orange';
  if (['Rejected', 'Failed'].includes(status)) return 'red';
  return 'gray';
};
